import { useI18N } from "@sundae/react-hooks";
import {
  Balance,
  ChevronDownIcon,
  Collapsible,
  ConditionalWrapper,
  FlexList,
  FlexListItem,
  Text,
  Tooltip,
  toolkitCx,
} from "@sundae/ui-toolkit";
import { AssetAmount } from "@sundaeswap/asset";
import { ADA_METADATA, EContractVersion } from "@sundaeswap/core";
import cx from "classnames";
import { FC, PropsWithChildren, memo, useMemo, useState } from "react";
import {
  ADA_TX_FEE,
  DEPOSIT,
  SCOOPER_FEE,
  SCOOPER_FEE_V3,
} from "../../../constants/SundaeSwap.constants";
import { useAppContext, useProfileAppContext } from "../../../stores";
import { useCreatePoolContext } from "../../../stores/LiquidityContext/CreatePoolContext/context";
import { useLiquidityContext } from "../../../stores/LiquidityContext/context";
import { useOrderContext } from "../../../stores/OrderContext";
import { IAssetMetaData } from "../../../types/Asset.types";

interface ILiquidityFeesWrapperProps {
  collapseLabel?: string;
  expandLabel?: string;
}

const LiquidityFeesWrapper: FC<PropsWithChildren<ILiquidityFeesWrapperProps>> = ({
  children,
  collapseLabel,
  expandLabel,
}) => {
  const { currentLocale, t } = useI18N("exchange");
  const [open, setOpen] = useState(false);
  const {
    state: { V3MaxScooperFee },
  } = useAppContext();
  const {
    state: { privacyEnabled },
  } = useProfileAppContext();
  const {
    state: { pool },
  } = useLiquidityContext();
  const {
    state: { contractVersion },
  } = useCreatePoolContext();
  const {
    state: {
      derived: { transaction },
    },
  } = useOrderContext();

  const V3MaxScooperFeeAmount = new AssetAmount<IAssetMetaData>(V3MaxScooperFee, ADA_METADATA);
  const isV3Pool = contractVersion === EContractVersion.V3 || pool?.version === EContractVersion.V3;
  const isCardanoTxFeeAvailable = transaction?.completeTx;

  const scooperFee = useMemo(() => {
    if (transaction?.builtTx?.fees?.scooperFee) {
      return transaction.builtTx.fees.scooperFee;
    }

    if (isV3Pool) {
      return V3MaxScooperFeeAmount || SCOOPER_FEE_V3;
    }

    return SCOOPER_FEE;
  }, [isV3Pool, V3MaxScooperFeeAmount, transaction?.builtTx?.fees?.scooperFee]);

  return (
    <Collapsible
      className="motion-safe:animate-slide-up"
      open={open}
      trigger={
        <div className="flex flex-col gap-2">
          {/* -------------------------------------------------------------------------------------------------
           * ADA Transaction Fee, Scooper Fee and Deposit
           * -----------------------------------------------------------------------------------------------*/}
          <FlexList>
            <FlexListItem
              field={{
                label: (
                  <Text size="sm" tag="span" variant="muted">
                    {t("fee", { context: "adaTx", ns: "fees" })}
                  </Text>
                ),
                variant: "muted",
              }}
              tooltip={t("fee", {
                context: transaction?.completeTx ? "adaTxExplained" : "adaTxExplainedNextStep",
                ns: "fees",
              })}
              value={{
                label: (
                  <div className="inline-flex gap-[2px]">
                    {!isCardanoTxFeeAvailable && (
                      <Text tag="span" size="sm">
                        ~{" "}
                      </Text>
                    )}
                    <Balance
                      abbreviate={false}
                      locale={currentLocale}
                      privacy={privacyEnabled}
                      showSymbol
                      size="sm"
                      value={
                        isCardanoTxFeeAvailable
                          ? transaction?.builtTx?.fees?.cardanoTxFee
                          : ADA_TX_FEE
                      }
                    />
                  </div>
                ),
              }}
            />

            <FlexListItem
              field={{
                label: (
                  <Text size="sm" tag="span" variant="muted">
                    {t("fee", { context: isV3Pool ? "scooperV3" : "scooper", ns: "fees" })}
                  </Text>
                ),
                variant: "muted",
              }}
              tooltip={t("fee", {
                context: isV3Pool ? "scooperV3Explained" : "scooperExplained",
                ns: "fees",
              })}
              value={{
                label: (
                  <div className="flex items-center gap-2">
                    {isV3Pool && (
                      <Balance
                        abbreviate={false}
                        className="line-through"
                        locale={currentLocale}
                        privacy={privacyEnabled}
                        showSymbol
                        size="sm"
                        value={SCOOPER_FEE_V3}
                        variant="error"
                      />
                    )}
                    <ConditionalWrapper
                      condition={isV3Pool}
                      wrapper={(children) => (
                        <Tooltip
                          tooltipLabel={t("fee", { context: "scooperV3Discount", ns: "fees" })}
                          trigger={<div className="inline-flex cursor-help">{children}</div>}
                        />
                      )}
                    >
                      <Balance
                        abbreviate={false}
                        locale={currentLocale}
                        privacy={privacyEnabled}
                        showSymbol
                        size="sm"
                        value={scooperFee}
                      />
                    </ConditionalWrapper>
                  </div>
                ),
              }}
            />

            <FlexListItem
              field={{
                label: (
                  <Text size="sm" tag="span" variant="muted">
                    {t("fee", { context: "deposit", ns: "fees" })}
                  </Text>
                ),
                variant: "muted",
              }}
              tooltip={t("fee", { context: "depositExplained", ns: "fees" })}
              value={{
                label: (
                  <Balance
                    abbreviate={false}
                    locale={currentLocale}
                    privacy={privacyEnabled}
                    showSymbol
                    size="sm"
                    value={transaction?.builtTx?.fees?.deposit ?? DEPOSIT}
                  />
                ),
              }}
            />
          </FlexList>

          <Text
            role="button"
            className={cx(
              "-ml-4 -mr-4 w-[calc(100%+2rem)] cursor-pointer rounded-lg py-1.5",
              toolkitCx.layout.spacing.horizontal.md,

              {
                [toolkitCx.layout.background.card]: !open,
                "bg-primary:20 dark:bg-primary/10": open,
              }
            )}
            tag="div"
            size="sm"
            variant="muted"
            onClick={() => setOpen((prev) => !prev)}
          >
            {open
              ? collapseLabel ?? t("less", { ns: "common" })
              : expandLabel ?? t("more", { ns: "common" })}{" "}
            <ChevronDownIcon
              className={cx(
                "inline h-4 w-4",
                {
                  "-scale-y-100": open,
                },
                toolkitCx.layout.text.muted,
                toolkitCx.layout.transition.transform
              )}
            />
          </Text>
        </div>
      }
    >
      <div className="mt-2">{children}</div>
    </Collapsible>
  );
};

export default memo(LiquidityFeesWrapper);
